import { Calendar, Check, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const BOOKING_URL_BEAUTY = "https://home.shortcutssoftware.com/ah#/_h/sites/6509/details";

const treatments = [
  {
    name: 'Skin Pen Microneedling',
    subtitle: 'The Gold Standard',
    description: 'Medical-grade microneedling that creates controlled micro-injuries to stimulate your skin\'s natural healing response. Boosts collagen and elastin production to dramatically improve acne scars, enlarged pores, fine lines, and overall skin texture. Often called the "gold standard" for skin rejuvenation.',
    benefits: ['Acne scar reduction', 'Pore refinement', 'Collagen stimulation', 'Improved skin texture'],
  },
  {
    name: 'LED Light Therapy',
    subtitle: 'Cellular Rejuvenation',
    description: 'Advanced medical-grade Red & Near-Infrared LED wavelengths (660nm-850nm) penetrate the skin at different depths to trigger real cellular change. Red light boosts collagen and reduces inflammation, while blue light targets acne-causing bacteria. Far more than a surface treatment — LED works at a mitochondrial level.',
    benefits: ['Boosts collagen production', 'Reduces redness & inflammation', 'Kills acne bacteria', 'Vagus nerve stimulation', 'Lowers cortisol & stress markers'],
  },
  {
    name: 'Professional Chemical Peels',
    subtitle: 'Intense Resurfacing',
    description: 'Our professional-grade chemical peels use carefully selected acids to remove damaged outer layers of skin, revealing fresh, glowing skin beneath. Excellent for sun damage, pigmentation, uneven skin tone, and fine lines. Treatments are customised to your skin type and concerns.',
    benefits: ['Brightens skin tone', 'Reduces pigmentation', 'Smooths fine lines', 'Resurfaces sun damage'],
  },
];

const skinConcerns = [
  { concern: 'Fine Lines & Wrinkles', solution: 'Microneedling, LED red light therapy' },
  { concern: 'Acne & Breakouts', solution: 'LED blue light, chemical peels' },
  { concern: 'Pigmentation & Sun Damage', solution: 'Chemical peels, microneedling' },
  { concern: 'Acne Scarring', solution: 'Skin Pen microneedling' },
  { concern: 'Dull & Uneven Skin', solution: 'Chemical peels, LED therapy' },
  { concern: 'Redness & Inflammation', solution: 'LED red light therapy' },
];

export default function Facials() {
  return (
    <main className="min-h-screen bg-[#FFFDF9]" data-testid="facials-page">
      <SEO page="facials" />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_1dc100c1-fd6e-4e96-8e6a-366ccfb0323b/artifacts/pmwdu00u_skicare%202%20lab.jpg"
            alt="La Biosthetique skincare facial treatment"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFFDF9] via-[#FFFDF9]/95 to-[#FFFDF9]/80" />
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#D4C4B0]/20 border border-[#D4C4B0]/50 rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-[#8D7B68]" />
              <span className="text-sm text-[#5D4A3A]">Advanced Skin Treatments</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-[#2C2420] leading-[1.1] mb-6">
              Facials & Skin Treatments
            </h1>
            <p className="text-lg text-[#5D4A3A] leading-relaxed mb-8">
              Advanced facial treatments in Camberwell including Microneedling, LED Light Therapy, and Professional Chemical Peels. Our results-driven approach combines clinical expertise with a luxury experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={BOOKING_URL_BEAUTY} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#4A5D44] hover:bg-[#3d4e38] text-white rounded-full px-8 py-4 text-lg" data-testid="facials-book-btn">
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Facial Treatment
                </Button>
              </a>
              <Link to="/beauty">
                <Button variant="outline" className="border-[#3D352D] text-[#3D352D] hover:bg-[#3D352D] hover:text-white rounded-full px-8 py-4 text-lg">
                  View All Beauty Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="py-16 bg-[#4A5D44]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <p className="text-3xl font-medium mb-2">15+</p>
              <p className="text-white/80">Years Experience</p>
            </div>
            <div>
              <p className="text-3xl font-medium mb-2">Medical</p>
              <p className="text-white/80">Grade Products</p>
            </div>
            <div>
              <p className="text-3xl font-medium mb-2">Custom</p>
              <p className="text-white/80">Treatment Plans</p>
            </div>
            <div>
              <p className="text-3xl font-medium mb-2">Results</p>
              <p className="text-white/80">Driven Approach</p>
            </div>
          </div>
        </div>
      </section>

      {/* Treatments Detail */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Our Treatments</p>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">Facial Treatments</h2>
            <p className="text-lg text-[#5D4A3A] max-w-2xl mx-auto">
              Each treatment is customised to your unique skin concerns after a thorough consultation with our skin specialists.
            </p>
          </div>

          <div className="space-y-8">
            {treatments.map((treatment) => (
              <div key={treatment.name} className="bg-white p-8 md:p-10 rounded-lg border border-[#D4C4B0]/50 hover:border-[#4A5D44]/50 transition-colors">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <p className="text-sm uppercase tracking-widest text-[#4A5D44] mb-2">{treatment.subtitle}</p>
                    <h3 className="text-2xl font-medium text-[#2C2420] mb-4">{treatment.name}</h3>
                    <p className="text-[#5D4A3A] leading-relaxed">{treatment.description}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#8D7B68] mb-3">Key Benefits</p>
                    <div className="space-y-2">
                      {treatment.benefits.map((benefit) => (
                        <div key={benefit} className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-[#4A5D44] flex-shrink-0" />
                          <span className="text-sm text-[#2C2420]">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The 20-Minute Nervous System Reset + LED */}
      <section className="py-24 md:py-32 bg-[#2C2420] text-white">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-[#D4C4B0] mb-4">Signature Treatment</p>
              <h2 className="text-4xl md:text-5xl font-medium mb-4">
                The 20-Minute Nervous System Reset + LED
              </h2>
              <p className="text-xl text-[#BCAAA4] italic mb-6">
                A high-tech escape for the modern mind and tired skin.
              </p>
              <p className="text-lg text-[#BCAAA4] leading-relaxed mb-8">
                This isn't just a facial; it's a recalibration. While medical-grade Red & Near-Infrared LED wavelengths work to boost your collagen and erase dullness, our specialised "Reset" protocol targets your nervous system.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-[#D4C4B0]" />
                  </div>
                  <div>
                    <p className="font-medium text-white">Vagus Nerve LED Placement</p>
                    <p className="text-sm text-[#BCAAA4]">Light is positioned to calm the body's primary "stress-reset" nerve.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-[#D4C4B0]" />
                  </div>
                  <div>
                    <p className="font-medium text-white">Deep Alpha-Wave Audio</p>
                    <p className="text-sm text-[#BCAAA4]">Optional noise-canceling headphones with binaural beats to shift your brain from "busy" to "still."</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-[#D4C4B0]" />
                  </div>
                  <div>
                    <p className="font-medium text-white">Weighted Eye Mask</p>
                    <p className="text-sm text-[#BCAAA4]">To ground the body and signal immediate safety to the brain.</p>
                  </div>
                </div>
              </div>
              <p className="text-[#BCAAA4] italic">
                The Result: A visible "lit-from-within" glow, reduced facial tension (the "Botox effect" without needles), and a profound sense of calm that lasts all day.
              </p>
            </div>
            <div className="bg-white/5 backdrop-blur rounded-lg p-8 border border-white/10">
              <p className="text-sm uppercase tracking-widest text-[#D4C4B0] mb-6">The Science: Why It Works</p>
              <div className="space-y-6">
                <div className="border-l-2 border-[#D4C4B0] pl-6">
                  <h4 className="font-medium text-white mb-2">Vagus Nerve Stimulation</h4>
                  <p className="text-sm text-[#BCAAA4]">Applying Red/Near-Infrared light to the neck and upper chest stimulates the vagus nerve, the "on-switch" for the parasympathetic (rest and digest) nervous system.</p>
                </div>
                <div className="border-l-2 border-[#D4C4B0] pl-6">
                  <h4 className="font-medium text-white mb-2">Mitochondrial Boost</h4>
                  <p className="text-sm text-[#BCAAA4]">The light (660nm-850nm) triggers ATP production, giving cells the energy to heal skin and repair nerves.</p>
                </div>
                <div className="border-l-2 border-[#D4C4B0] pl-6">
                  <h4 className="font-medium text-white mb-2">Cortisol Drop</h4>
                  <p className="text-sm text-[#BCAAA4]">A 20-minute session has been shown to lower physiological stress markers and improve Heart Rate Variability (HRV).</p>
                </div>
              </div>
              <div className="mt-8">
                <a href={BOOKING_URL_BEAUTY} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-[#4A5D44] hover:bg-[#3d4e38] text-white rounded-full py-4 text-lg" data-testid="led-reset-book-btn">
                    <Calendar className="w-5 h-5 mr-2" />
                    Book Your Reset
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skin Concerns */}
      <section className="py-24 md:py-32 bg-[#E8DED2]/50">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-[#4A5D44] mb-4">Targeted Solutions</p>
              <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
                Whatever Your Skin Concern
              </h2>
              <p className="text-lg text-[#5D4A3A] leading-relaxed mb-8">
                Our experienced skin specialists will assess your skin and recommend the best treatment plan for your specific concerns. Every facial at Artistry House is customized to your unique needs.
              </p>
              <div className="space-y-4">
                {skinConcerns.map((item) => (
                  <div key={item.concern} className="flex items-start gap-3 bg-white p-4 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-[#4A5D44]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-[#4A5D44]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#2C2420]">{item.concern}</p>
                      <p className="text-sm text-[#5D4A3A]">{item.solution}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="aspect-square rounded-lg overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1552693673-1bf958298935?w=600&h=600&fit=crop"
                alt="Skin rejuvenation facial Camberwell"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#2C2420]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-4xl md:text-5xl font-medium text-white mb-6">
            Ready for Glowing Skin?
          </h2>
          <p className="text-lg text-[#BCAAA4] max-w-2xl mx-auto mb-8">
            Book a facial at our Camberwell salon. Our skin specialists will create a treatment plan tailored to your skin goals.
          </p>
          <a href={BOOKING_URL_BEAUTY} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#4A5D44] hover:bg-[#3d4e38] text-white rounded-full px-10 py-6 text-lg" data-testid="facials-book-cta-btn">
              <Calendar className="w-5 h-5 mr-2" />
              Book Facial Treatment
            </Button>
          </a>
        </div>
      </section>
    </main>
  );
}
